// src/project/ProjectSerializer.js
// Pure functions for converting between app state and .helix JSON format.

import * as Tone from 'tone';

/**
 * Serialize the current app state into a .helix JSON-compatible object.
 *
 * @param {object} trackManager - TrackManager singleton
 * @param {object} clipLibrary - ClipLibrary singleton
 * @param {object} engine - AudioEngine singleton
 * @param {string} projectName
 * @param {import('../instruments/CustomInstrumentRegistry.js').CustomInstrumentRegistry} customRegistry
 * @param {object} [options]
 * @param {boolean} [options.forExport] - Use relative paths instead of IDB refs
 * @returns {object} JSON-serializable .helix file data
 */
export function serialize(trackManager, clipLibrary, engine, projectName, customRegistry, options = {}) {
  const now = new Date().toISOString();

  const usedBuiltinIds = new Set();
  for (const t of trackManager.tracks) {
    if (t.instrumentId && !customRegistry.get(t.instrumentId)) {
      usedBuiltinIds.add(t.instrumentId);
    }
  }

  const instruments = [
    ...Array.from(usedBuiltinIds).map(id => ({ type: 'builtin', id })),
    ...customRegistry.getAll().map(c => ({
      type: 'custom',
      id: c.id,
      name: c.name || 'Untitled',
      sampleMap: { ...c.sampleMap },
      release: c.release ?? 1.2,
      color: c.color || '#888'
    }))
  ];

  const sampleManifest = [];
  for (const c of customRegistry.getAll()) {
    for (const [note, ref] of Object.entries(c.sampleMap)) {
      const entry = { ref, name: `${c.name}-${note}`, note };
      if (options.forExport) entry.path = `samples/${ref}.wav`;
      sampleManifest.push(entry);
    }
  }

  return {
    format: 'helix-composer',
    version: 1,
    project: {
      name: projectName || 'Untitled',
      created: options.projectCreated || '',
      modified: now
    },
    engine: {
      bpm: Tone.Transport.bpm.value,
      reverbWet: engine.reverb ? engine.reverb.wet.value : 0.3,
      humanizeTiming: engine.humanizeAmount,
      humanizeVelocity: engine.velocityJitter
    },
    instruments,
    tracks: trackManager.tracks.map(t => ({
      instrumentRef: t.instrumentId,
      name: t.name,
      mixer: { ...t.mixer },
      notes: t.notes.map(n => ({
        time: n.time, duration: n.duration, pitch: n.pitch, velocity: n.velocity
      })),
      clips: (t.clips || []).map(c => ({
        clipId: c.clipId, startTime: c.startTime, repeatCount: c.repeatCount
      }))
    })),
    clipLibrary: (clipLibrary.definitions || []).map(d => ({
      id: d.id,
      name: d.name,
      notes: d.notes.map(n => ({
        time: n.time, duration: n.duration, pitch: n.pitch, velocity: n.velocity
      }))
    })),
    sampleManifest
  };
}

/**
 * Deserialize a .helix JSON object into app state fragments.
 * @param {object} json - Parsed .helix file data
 * @returns {{ project: object, engine: object, instruments: object[],
 *            tracks: object[], clipLibrary: object[], sampleManifest: object[] }}
 * @throws {Error} If format/version is invalid
 */
export function deserialize(json) {
  if (!json || json.format !== 'helix-composer') {
    throw new Error('Invalid project file: wrong format');
  }
  if (typeof json.version !== 'number' || json.version < 1) {
    throw new Error(`Unsupported project version: ${json.version}`);
  }

  return {
    project: {
      name: json.project?.name || 'Untitled',
      created: json.project?.created || new Date().toISOString(),
      modified: json.project?.modified || new Date().toISOString()
    },
    engine: {
      bpm: json.engine?.bpm ?? 100,
      reverbWet: json.engine?.reverbWet ?? 0.3,
      humanizeTiming: json.engine?.humanizeTiming ?? 0.012,
      humanizeVelocity: json.engine?.humanizeVelocity ?? 0.08
    },
    instruments: json.instruments || [],
    tracks: json.tracks || [],
    clipLibrary: json.clipLibrary || [],
    sampleManifest: json.sampleManifest || []
  };
}

/**
 * Validate that all instrument references resolve.
 * Returns array of error messages (empty if valid).
 */
export function validateRefs(data) {
  const errors = [];
  const instrumentList = data.instruments || [];
  const trackList = data.tracks || [];
  const ids = new Set(instrumentList.map(i => i.id));
  for (const t of trackList) {
    if (!ids.has(t.instrumentRef)) {
      errors.push(`Track "${t.name}" references unknown instrument "${t.instrumentRef}"`);
    }
  }

  // Check clipId references
  const clipIds = new Set((data.clipLibrary || []).map(c => c.id));
  for (const t of trackList) {
    for (const ci of (t.clips || [])) {
      if (!clipIds.has(ci.clipId)) {
        errors.push(`Track "${t.name}" references unknown clip "${ci.clipId}"`);
      }
    }
  }

  // Check sample ref integrity
  const refsInManifest = new Set((data.sampleManifest || []).map(s => s.ref));
  for (const inst of instrumentList) {
    if (inst.type === 'custom' && inst.sampleMap) {
      for (const [note, ref] of Object.entries(inst.sampleMap)) {
        if (!refsInManifest.has(ref)) {
          errors.push(`Custom instrument "${inst.name}" references unknown sample "${ref}" for note ${note}`);
        }
      }
    }
  }
  return errors;
}
